import { formatDate } from 'date-fns';
import { defineStore } from 'pinia';

import {
  client,
  getArticles,
  type GetPopularArticlesData,
  type GetArticlesData,
  type GetArticlesResponse,
} from '@/shared/api/experience';
import type { ArticleInList } from '@/shared/models/articleInList';
import { normalizeArticleInList } from '@/shared/transforms/articleInList';
import { useLogger } from '@/shared/utils/logger';

export type GetJournalContentParams = Omit<GetArticlesData['query'], 'page'>;
export type GetPopularArticlesParams = Omit<GetPopularArticlesData['query'], 'sorting'> &
  Partial<Pick<GetPopularArticlesData['query'], 'sorting'>>;
export type GetArticlesParams = GetArticlesData['query'];

type SlugType = 'region' | 'country' | 'city';
type PopularArticlesSorting = GetPopularArticlesData['query']['sorting'];
type JournalSlugQueryKey = 'region_slug' | 'country_slug' | 'city_slug';

const slugTypeToQueryKey: Record<SlugType, JournalSlugQueryKey> = {
  region: 'region_slug',
  country: 'country_slug',
  city: 'city_slug',
};

const mapPopularSortingToOrdering = (sorting?: PopularArticlesSorting) => {
  if (sorting === '-date') {
    return '-published_at';
  }

  return '-pageviews_sorting';
};

export const useJournalContent = defineStore('journal:content', {
  state: () => ({
    /**
     * Список статей или достопримечательностей.
     */
    articlesState: [] as ArticleInList[],

    /**
     * Номер текущей страницы.
     */
    currentPage: 1,

    /**
     * Общее количество статей в экзмепляре сущности.
     */
    totalCount: 0,

    /**
     * Признак наличия незагруженных статей.
     */
    hasNextArticles: false,

    /**
     * Флаг индикации загрузки.
     */
    isLoading: false,

    /**
     * Флаг, показывающий, что загрузка окончилать неудачей.
     */
    isLoadingFailed: false,
  }),

  getters: {
    /**
     * Список статей или достопримечательностей.
     */
    articles: (state) => state.articlesState,

    /**
     * Номер текущей страницы.
     */
    page: (state) => state.currentPage,

    /**
     * Общее количество статей в экзмепляре сущности.
     */
    totalArticlesCount: (state) => state.totalCount,

    /**
     * Признак наличия незагруженных статей.
     */
    hasNext: (state) => state.hasNextArticles,

    /**
     * Флаг индикации загрузки.
     */
    loading: (state) => state.isLoading,

    /**
     * Флаг, показывающий, что загрузка окончилать неудачей.
     */
    loadingFailed: (state) => state.isLoadingFailed,
  },

  actions: {
    logger: useLogger,

    /**
     * Загружает список статей автора.
     *
     * @param authorId идентификатор автора
     * @param params дополнительные параметры запроса
     */
    async getItemsList(authorId: { authorId: number }, params: GetJournalContentParams) {
      // TODO: add autoreset on filter changing
      let response;

      this.isLoading = true;
      this.isLoadingFailed = false;

      try {
        response = await client.get<GetArticlesResponse>({
          url: '/service/journal/api/v1/articles',
          query: { author_id: authorId.authorId, page: this.currentPage, ...params },
        });
      } catch (e) {
        this.logger().error(e);
        this.isLoading = false;
        this.isLoadingFailed = true;

        return;
      }

      const normalizedArticles = response.data?.results?.map((article) => normalizeArticleInList(article)) ?? [];

      this.articlesState = this.articles.length ? [...this.articles, ...normalizedArticles] : normalizedArticles;
      this.totalCount = response.data?.count ?? 0;
      this.currentPage += 1;
      this.hasNextArticles = Boolean(response.data?.next);
      this.isLoading = false;
    },

    /**
     * Загружает список популярных статей.
     *
     * @param params дополнительные параметры запроса
     */
    async getPopularItemsList(params: GetPopularArticlesParams) {
      const dateNow: Date = new Date();
      const changedDate: number = dateNow.setDate(dateNow.getDate() - 60);
      const dateFormat: string = 'yyyy-MM-dd';
      const formattedDate: string = formatDate(changedDate, dateFormat);

      let response;

      this.isLoading = true;
      this.isLoadingFailed = false;

      const { sorting, ...restParams } = params;
      const ordering = mapPopularSortingToOrdering(sorting);

      try {
        response = await client.get<GetArticlesResponse>({
          url: '/service/journal/api/v1/articles',
          query: {
            type: 'article',
            ordering,
            page: this.currentPage,
            published_after: formattedDate,
            ...restParams,
          },
        });
      } catch (e) {
        this.logger().error(e);
        this.isLoading = false;
        this.isLoadingFailed = true;

        return;
      }

      const normalizedArticles = response.data?.results?.map((article) => normalizeArticleInList(article)) ?? [];

      this.articlesState = this.articles.length ? [...this.articles, ...normalizedArticles] : normalizedArticles;
      this.totalCount = response.data?.count ?? 0;
      this.currentPage += 1;
      this.hasNextArticles = Boolean(response.data?.next);
      this.isLoading = false;
    },

    /**
     * Загружает список статей.
     *
     * @param params дополнительные параметры запроса
     */
    async getArticles(params: {
      page?: number;
      pageSize: number;
      slugType?: SlugType;
      slug?: string;
      is_ugc?: boolean;
    }) {
      let response;

      this.isLoading = true;

      const query: GetArticlesData['query'] = {
        type: 'article',
        page: params.page ?? this.currentPage,
        page_size: params.pageSize,
      };

      if (params.slugType && params.slug) {
        query[slugTypeToQueryKey[params.slugType]] = params.slug;
      }

      if (params.slugType === 'country') {
        query.ordering = '-pageviews_sorting';
      }

      if (params.is_ugc) {
        query.is_ugc = true;
      }

      try {
        response = await getArticles({ query });
      } catch (e) {
        this.logger().error(e);
        this.isLoading = false;
        this.isLoadingFailed = true;

        return;
      }

      const normalizedArticles = response.data?.results?.map((article) => normalizeArticleInList(article)) ?? [];

      this.articlesState = this.articles.length ? [...this.articles, ...normalizedArticles] : normalizedArticles;
      this.totalCount = response.data?.count ?? 0;
      this.currentPage += 1;
      this.hasNextArticles = Boolean(response.data?.next);
      this.isLoading = false;
    },

    /**
     * Сбрасывает состояние экземпляра сущности.
     */
    reset() {
      this.articlesState = [];
      this.currentPage = 1;
      this.totalCount = 0;
      this.hasNextArticles = false;
      this.isLoading = false;
      this.isLoadingFailed = false;
    },
  },
});
